import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import LocalStorageHelper from '~/client/src/shared/utils/LocalStorageHelper'

/**
 * A per-user preference that is remembered per project — stored as one
 * `{ [projectId]: boolean }` blob per key, so a write has to merge rather than replace.
 */
type IProjectFlagMap = { [projectId: string]: boolean }

// Anything unparseable reads as "no preference recorded" rather than throwing: a corrupted blob
// must not take a map down, and the next write repairs it.
const readMap = (userId: string, key: LocalStorageKey): IProjectFlagMap => {
  if (!userId) {
    return {}
  }
  const raw = LocalStorageHelper.getUserPreferenceByKey(userId, key)
  try {
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

/** `undefined` means the user has expressed no preference for this project. */
export const readPerProjectFlag = (
  userId: string,
  projectId: string,
  key: LocalStorageKey,
): boolean | undefined => {
  if (!userId || !projectId) {
    return undefined
  }
  const stored = readMap(userId, key)[projectId]
  return typeof stored === 'boolean' ? stored : undefined
}

export const writePerProjectFlag = (
  userId: string,
  projectId: string,
  key: LocalStorageKey,
  value: boolean,
): void => {
  if (!userId || !projectId) {
    return
  }
  LocalStorageHelper.setUserPreferences(userId, {
    [key]: JSON.stringify({ ...readMap(userId, key), [projectId]: value }),
  })
}
